<template>
  <div class="p-4">
    <!-- Состояние загрузки -->
    <div v-if="loading" class="flex justify-center items-center h-40">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <!-- Кнопка "Добавить модуль" -->
    <div class="flex space-x-2 mb-4">
      <button
        v-can-write="'modules'"
        @click="openAddModuleModal"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        Добавить модуль
      </button>
      <button
        v-if="isAdmin"
        @click="openSettingsModal"
        class="px-4 py-2 text-sm font-medium text-white bg-slate-700 rounded-md hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500"
      >
        Настройки складов
      </button>
    </div>

    <!-- Таблица модулей -->
    <table v-if="!loading" class="w-full border-collapse bg-white shadow-md">
      <thead class="bg-gray-100">
        <tr>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">ID</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Название</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Тип модуля</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Действия</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="module in modules" :key="module.id" class="border-b hover:bg-gray-50">
          <td class="px-4 py-2 text-sm text-gray-700">{{ module.id }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">
            <router-link
              :to="{ name: 'ModuleDetails', params: { id: module.id } }"
              class="text-blue-600 hover:text-blue-800 underline"
            >
              {{ module.name }}
            </router-link>
          </td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ module.module_type.name }}</td>
          <td class="px-4 py-2">
            <button
              v-can-write="'modules'"
              @click="confirmDelete(module.id)"
              class="px-2 py-1 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Удалить
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Модальное окно для добавления модуля -->
    <AddModuleModal ref="addModuleModal" @module-added="getModules"></AddModuleModal>

    <!-- Модальное окно подтверждения удаления -->
    <div v-if="isConfirmModalVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">Подтвердите удаление</h2>
        <p class="text-sm text-gray-700 mb-4">Вы уверены, что хотите удалить этот модуль?</p>
        <div class="flex justify-end space-x-2">
          <button
            @click="cancelDelete"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Отмена
          </button>
          <button
            @click="proceedDelete"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Удалить
          </button>
        </div>
      </div>
    </div>

    <!-- Модалка: настройки складов (все module_settings, прямое построчное редактирование) — только admin@admin.ru -->
    <div v-if="settingsModalVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="closeSettingsModal">
      <div class="bg-white rounded-lg shadow-xl w-[95%] max-w-6xl max-h-[90vh] flex flex-col">
        <div class="flex items-center justify-between px-6 py-4 border-b">
          <h2 class="text-lg font-bold">Настройки складов</h2>
          <button @click="closeSettingsModal" class="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div class="px-6 py-4 overflow-y-auto">
          <div v-if="settingsLoading" class="flex justify-center items-center h-40">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
          <table v-else class="w-full text-sm border-collapse">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-3 py-2 text-left font-medium text-gray-700">ID</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700">Модуль</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700">key</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700">type</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700">value_text</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700">value_number</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700">value_bool</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in settings" :key="row.id" class="border-b">
                <td class="px-3 py-2 text-gray-700">{{ row.id }}</td>
                <td class="px-3 py-2 text-gray-700">
                  {{ row.module_name || '—' }}
                  <span class="text-xs text-gray-400">#{{ row.module_id }}</span>
                </td>
                <template v-if="editingId === row.id">
                  <td class="px-3 py-2"><input v-model="editDraft.key" type="text" class="w-full px-2 py-1 border rounded" /></td>
                  <td class="px-3 py-2"><input v-model="editDraft.type" type="text" class="w-full px-2 py-1 border rounded" /></td>
                  <td class="px-3 py-2"><input v-model="editDraft.value_text" type="text" class="w-full px-2 py-1 border rounded" /></td>
                  <td class="px-3 py-2"><input v-model.number="editDraft.value_number" type="number" step="any" class="w-28 px-2 py-1 border rounded" /></td>
                  <td class="px-3 py-2">
                    <select v-model="editDraft.value_bool" class="px-2 py-1 border rounded">
                      <option :value="null">—</option>
                      <option :value="true">true</option>
                      <option :value="false">false</option>
                    </select>
                  </td>
                  <td class="px-3 py-2 whitespace-nowrap">
                    <button @click="saveRow" :disabled="savingId === row.id" class="px-3 py-1 text-white bg-green-600 rounded hover:bg-green-700 disabled:bg-gray-400">
                      {{ savingId === row.id ? 'Сохранение…' : 'Сохранить' }}
                    </button>
                    <button @click="cancelEdit" class="ml-2 px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">Отмена</button>
                  </td>
                </template>
                <template v-else>
                  <td class="px-3 py-2 text-gray-700">{{ row.key || '—' }}</td>
                  <td class="px-3 py-2 text-gray-700">{{ row.type || '—' }}</td>
                  <td class="px-3 py-2 text-gray-700">{{ row.value_text ?? '—' }}</td>
                  <td class="px-3 py-2 text-gray-700">{{ row.value_number ?? '—' }}</td>
                  <td class="px-3 py-2 text-gray-700">{{ row.value_bool === null ? '—' : row.value_bool }}</td>
                  <td class="px-3 py-2 whitespace-nowrap">
                    <button @click="startEdit(row)" class="px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700">Редактировать</button>
                    <button @click="deleteRow(row)" :disabled="deletingId === row.id" class="ml-2 px-3 py-1 text-white bg-red-600 rounded hover:bg-red-700 disabled:bg-gray-400">
                      {{ deletingId === row.id ? 'Удаление…' : 'Удалить' }}
                    </button>
                  </td>
                </template>
              </tr>
              <tr v-if="!settings.length">
                <td colspan="8" class="px-3 py-6 text-center text-gray-400">Нет настроек</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="px-6 py-4 border-t flex justify-end">
          <button @click="closeSettingsModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">Закрыть</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AddModuleModal from '@/components/AddModuleModal.vue';

export default {
  components: { AddModuleModal },
  data() {
    return {
      modules: [], // Список модулей
      loading: true, // Флаг загрузки
      isConfirmModalVisible: false, // Флаг видимости модального окна подтверждения
      moduleIdToDelete: null, // ID модуля для удаления
      isAdmin: false, // Кнопка/модалка настроек только для admin@admin.ru
      settingsModalVisible: false,
      settingsLoading: false,
      settings: [], // Все записи module_settings
      editingId: null, // ID строки в режиме редактирования
      editDraft: {}, // Черновик редактируемой строки
      savingId: null, // ID сохраняемой строки
      deletingId: null, // ID удаляемой строки
    };
  },
  created() {
    this.getModules();
    this.checkAdmin();
  },
  methods: {
    getModules() {
      axios
        .post('/api/module/getModules')
        .then((response) => {
          this.modules = response.data.data;
          this.loading = false;
        })
        .catch((error) => {
          console.error(error);
        });
    },

    confirmDelete(id) {
      this.moduleIdToDelete = id; // Сохраняем ID модуля
      this.isConfirmModalVisible = true; // Показываем модальное окно
    },

    cancelDelete() {
      this.isConfirmModalVisible = false; // Скрываем модальное окно
      this.moduleIdToDelete = null; // Очищаем ID
    },

    proceedDelete() {
      if (!this.moduleIdToDelete) {
        console.error('ID модуля не указан');
        return;
      }

      axios
        .post('/api/module/deleteModule', { id: this.moduleIdToDelete })
        .then(() => {
          this.getModules(); // Обновляем список модулей
          this.cancelDelete(); // Скрываем модальное окно
        })
        .catch((error) => {
          console.error(error);
        });
    },

    openAddModuleModal() {
      if (!this.$refs.addModuleModal) {
        return;
      }
      this.$refs.addModuleModal.show();
    },

    checkAdmin() {
      axios
        .get('/api/user')
        .then((response) => {
          this.isAdmin = response.data?.email === 'admin@admin.ru';
        })
        .catch(() => {
          this.isAdmin = false;
        });
    },

    openSettingsModal() {
      this.settingsModalVisible = true;
      this.loadSettings();
    },

    closeSettingsModal() {
      this.settingsModalVisible = false;
      this.cancelEdit();
    },

    loadSettings() {
      this.settingsLoading = true;
      axios
        .get('/api/module-settings/all')
        .then((response) => {
          this.settings = response.data.data || [];
        })
        .catch((error) => {
          console.error(error);
          const msg = error.response?.data?.message || 'Не удалось загрузить настройки';
          alert(msg);
        })
        .finally(() => {
          this.settingsLoading = false;
        });
    },

    startEdit(row) {
      this.editingId = row.id;
      this.editDraft = { ...row };
    },

    cancelEdit() {
      this.editingId = null;
      this.editDraft = {};
    },

    saveRow() {
      this.savingId = this.editingId;
      axios
        .post('/api/module-settings/update-row', {
          id: this.editDraft.id,
          key: this.editDraft.key,
          type: this.editDraft.type,
          value_text: this.editDraft.value_text,
          value_number: this.editDraft.value_number,
          value_bool: this.editDraft.value_bool,
        })
        .then(() => {
          const idx = this.settings.findIndex((s) => s.id === this.editDraft.id);
          if (idx !== -1) {
            this.settings.splice(idx, 1, { ...this.settings[idx], ...this.editDraft });
          }
          this.cancelEdit();
        })
        .catch((error) => {
          console.error(error);
          const msg = error.response?.data?.message || 'Не удалось сохранить настройку';
          alert(msg);
        })
        .finally(() => {
          this.savingId = null;
        });
    },

    deleteRow(row) {
      if (!confirm(`Удалить настройку #${row.id} (${row.key || '—'}) модуля «${row.module_name || row.module_id}»?`)) {
        return;
      }
      this.deletingId = row.id;
      axios
        .post('/api/module-settings/delete-row', { id: row.id })
        .then(() => {
          const idx = this.settings.findIndex((s) => s.id === row.id);
          if (idx !== -1) {
            this.settings.splice(idx, 1);
          }
          if (this.editingId === row.id) {
            this.cancelEdit();
          }
        })
        .catch((error) => {
          console.error(error);
          const msg = error.response?.data?.message || 'Не удалось удалить настройку';
          alert(msg);
        })
        .finally(() => {
          this.deletingId = null;
        });
    },
  },
};
</script>
